import React, { useState, useEffect, useMemo } from "react";
import Plot from "react-plotly.js";
import ReactApexChart from "react-apexcharts";
import { motion } from "framer-motion";
import "./Dashboard.css";

const ChartTypeSelector = ({ currentType, onTypeChange, availableTypes }) => (
  <div className="chart-type-selector">
    {availableTypes.map((type) => (
      <button
        key={type.value}
        className={`chart-type-btn ${currentType === type.value ? 'active' : ''}`}
        onClick={() => onTypeChange(type.value)}
        title={type.label}
      >
        {type.icon}
      </button>
    ))}
  </div>
);

const ChartMetrics = ({ data, title }) => {
  const metrics = useMemo(() => {
    if (!data || !Array.isArray(data)) return null;
    
    const values = data.filter(v => typeof v === 'number' && !isNaN(v));
    if (values.length === 0) return null;
    
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    
    return { sum, avg, max, min, count: values.length };
  }, [data]);

  if (!metrics) return null;

  return (
    <div className="chart-metrics">
      <div className="metric">
        <span className="metric-label">Total</span>
        <span className="metric-value">₹{metrics.sum.toLocaleString()}</span>
      </div>
      <div className="metric">
        <span className="metric-label">Average</span>
        <span className="metric-value">₹{metrics.avg.toLocaleString()}</span>
      </div>
      <div className="metric">
        <span className="metric-label">Peak</span>
        <span className="metric-value">₹{metrics.max.toLocaleString()}</span>
      </div>
      <div className="metric">
        <span className="metric-label">Records</span>
        <span className="metric-value">{metrics.count}</span>
      </div>
    </div>
  );
};

function EnhancedChart({ chartData, title, preferredType = "auto", showMetrics = true }) {
  const [chartType, setChartType] = useState(preferredType);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsLoading(false);
  }, [chartData]);

  const availableChartTypes = [
    { value: "auto", label: "Auto", icon: "🔄" },
    { value: "plotly", label: "Interactive", icon: "📊" },
    { value: "apex", label: "Modern", icon: "📈" },
  ];

  // Define conversion functions first
  const convertToPlotly = (labels, values, dataset, type, title) => {
    const isLine = type === "line";
    const isPie = type === "pie";
    
    // Common configuration
    const commonConfig = {
      responsive: true,
      displaylogo: false,
      modeBarButtonsToRemove: ["autoScale2d"],
      toImageButtonOptions: {
        format: 'png',
        filename: title.toLowerCase().replace(/\s+/g, '_'),
        height: 800,
        width: 1200,
        scale: 2
      }
    };

    // Enhanced layout with better styling
    const layout = {
      title: {
        text: title,
        font: { size: 20, family: "Inter, sans-serif", color: "#1f2937" },
        x: 0.05,
        y: 0.95,
      },
      template: "plotly_white",
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(0,0,0,0)",
      font: {
        family: "Inter, sans-serif",
        size: 12,
        color: "#374151",
      },
      autosize: true,
      height: 520,
      margin: { t: 80, r: 60, b: 100, l: 80 },
      hovermode: "x unified",
      hoverlabel: { 
        bgcolor: "#ffffff", 
        bordercolor: "#e5e7eb",
        font: { size: 12 },
        namelength: -1
      },
      legend: { 
        orientation: "h", 
        y: -0.2,
        x: 0.5,
        xanchor: 'center'
      },
      annotations: []
    };

    if (isPie) {
      return {
        data: [{
          type: "pie",
          labels,
          values,
          hole: 0.4,
          textinfo: "label+percent",
          textposition: "outside",
          hovertemplate: "<b>%{label}</b><br>₹%{value:,.0f} (%{percent})<extra></extra>",
          marker: {
            colors: [
              '#0ea5e9', '#a855f7', '#22c55e', '#f59e0b', '#ef4444',
              '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899'
            ],
            line: { color: '#ffffff', width: 2 }
          }
        }],
        layout: {
          ...layout,
          showlegend: true,
          legend: { orientation: "v", x: 1.02, y: 0.5 }
        },
        config: commonConfig
      };
    }

    // Line or Bar chart
    const trace = {
      x: labels,
      y: values,
      type: isLine ? "scatter" : "bar",
      mode: isLine ? "lines+markers" : undefined,
      name: dataset.label || title,
      line: isLine ? {
        color: "#0ea5e9",
        width: 3,
        shape: "spline",
        smoothing: 0.6
      } : undefined,
      marker: {
        color: isLine ? "#0ea5e9" : 
               labels.map((_, i) => `hsl(${(i * 360 / labels.length) % 360}, 70%, 55%)`),
        size: isLine ? 8 : undefined,
        line: { color: "#ffffff", width: 1 },
        opacity: 0.8
      },
      fill: isLine ? "tonexty" : undefined,
      fillcolor: isLine ? "rgba(14, 165, 233, 0.1)" : undefined,
      hovertemplate: isLine 
        ? "%{x}<br>₹%{y:,.0f}<extra></extra>"
        : "%{x}<br>₹%{y:,.0f}<extra></extra>",
    };

    // Add moving average for line charts
    const traces = [trace];
    if (isLine && values.length > 7) {
      const movingAvg = values.map((_, i) => {
        const start = Math.max(0, i - 3);
        const slice = values.slice(start, i + 4).filter(v => typeof v === 'number');
        return slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : null;
      });

      traces.push({
        x: labels,
        y: movingAvg,
        type: "scatter",
        mode: "lines",
        name: "Moving Average",
        line: { color: "#a855f7", width: 2, dash: "dot" },
        hovertemplate: "%{x}<br>MA: ₹%{y:,.0f}<extra></extra>",
      });
    }

    // Enhanced axis styling
    layout.xaxis = {
      title: { text: "", font: { size: 14 } },
      showgrid: true,
      gridcolor: "#f3f4f6",
      tickangle: labels.some(l => String(l).length > 8) ? -45 : 0,
      automargin: true,
      tickfont: { size: 11 }
    };

    layout.yaxis = {
      title: { text: "Amount (₹)", font: { size: 14 } },
      showgrid: true,
      gridcolor: "#f9fafb",
      tickformat: ",.0f",
      tickprefix: "₹",
      automargin: true,
      rangemode: "tozero"
    };

    return { data: traces, layout, config: commonConfig };
  };

  const convertToApex = (labels, values, dataset, type, title) => {
    const isLine = type === "line";
    const isPie = type === "pie";

    if (isPie) {
      return {
        series: values,
        options: {
          chart: {
            type: 'donut',
            height: 520,
            animations: {
              enabled: true,
              easing: 'easeinout',
              speed: 800
            },
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false
              }
            }
          },
          labels,
          colors: [
            '#0ea5e9', '#a855f7', '#22c55e', '#f59e0b', '#ef4444',
            '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899'
          ],
          plotOptions: {
            pie: {
              donut: {
                size: '60%',
                labels: {
                  show: true,
                  total: {
                    show: true,
                    label: 'Total',
                    formatter: (w) => '₹' + w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString()
                  }
                }
              }
            }
          },
          dataLabels: {
            enabled: true,
            formatter: (val) => val.toFixed(1) + '%'
          },
          legend: {
            position: 'bottom',
            fontSize: '12px'
          },
          tooltip: {
            y: {
              formatter: (val) => '₹' + val.toLocaleString()
            }
          },
          title: {
            text: title,
            style: {
              fontSize: '20px',
              fontFamily: 'Inter, sans-serif',
              color: '#1f2937'
            }
          }
        }
      };
    }

    // Line or Bar chart
    return {
      series: [{
        name: dataset.label || title,
        data: values
      }],
      options: {
        chart: {
          type: isLine ? 'area' : 'bar',
          height: 520,
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          zoom: {
            enabled: true,
            type: 'x'
          }
        },
        colors: ['#0ea5e9'],
        dataLabels: {
          enabled: false
        },
        stroke: isLine ? {
          curve: 'smooth',
          width: 3
        } : {
          width: 0
        },
        fill: isLine ? {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            type: 'vertical',
            colorStops: [
              { offset: 0, color: '#0ea5e9', opacity: 0.3 },
              { offset: 100, color: '#0ea5e9', opacity: 0.05 }
            ]
          }
        } : {
          opacity: 0.8
        },
        grid: {
          borderColor: '#f3f4f6',
          strokeDashArray: 3
        },
        xaxis: {
          categories: labels,
          labels: {
            rotate: labels.some(l => String(l).length > 8) ? -45 : 0,
            style: {
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif'
            }
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            formatter: (val) => '₹' + val.toLocaleString(),
            style: {
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif'
            }
          },
          title: {
            text: 'Amount (₹)',
            style: {
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif'
            }
          }
        },
        tooltip: {
          theme: 'light',
          x: {
            show: true
          },
          y: {
            formatter: (val) => '₹' + val.toLocaleString()
          },
          style: {
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif'
          }
        },
        title: {
          text: title,
          align: 'left',
          style: {
            fontSize: '20px',
            fontFamily: 'Inter, sans-serif',
            color: '#1f2937'
          }
        }
      }
    };
  };

  // Process chart data
  const processedData = useMemo(() => {
    if (!chartData) return null;
    
    if (chartData.error) {
      setError(chartData.error);
      return null;
    }

    try {
      // Handle Plotly format
      if (chartData.chart) {
        const parsed = JSON.parse(chartData.chart);
        return {
          type: "plotly",
          data: parsed.data,
          layout: parsed.layout,
          config: {
            responsive: true,
            displaylogo: false,
            modeBarButtonsToRemove: ["autoScale2d", "pan2d", "select2d", "lasso2d"],
            toImageButtonOptions: {
              format: 'png',
              filename: title.toLowerCase().replace(/\s+/g, '_'),
              height: 800,
              width: 1200,
              scale: 2
            }
          }
        };
      }

      // Handle Chart.js format - convert to both Plotly and ApexCharts
      if (chartData.data && chartData.data.datasets) {
        const dataset = chartData.data.datasets[0];
        const labels = chartData.data.labels || [];
        const values = dataset.data || [];
        const chartTypeLower = (chartData.type || "").toLowerCase();

        return {
          type: "dual",
          labels,
          values,
          originalType: chartTypeLower,
          dataset,
          plotlyData: convertToPlotly(labels, values, dataset, chartTypeLower, title),
          apexData: convertToApex(labels, values, dataset, chartTypeLower, title)
        };
      }

      return null;
    } catch (err) {
      setError(`Error processing chart data: ${err.message}`);
      return null;
    }
  }, [chartData, title]);

  const renderChart = () => {
    if (error) {
      return (
        <div className="chart-error">
          <h3>{title}</h3>
          <p>❌ {error}</p>
        </div>
      );
    }

    if (!processedData) {
      return (
        <div className="chart-error">
          <h3>{title}</h3>
          <p>❌ No chart data available</p>
        </div>
      );
    }

    const activeType = chartType === "auto" 
      ? (processedData.type === "dual" ? "plotly" : processedData.type)
      : chartType;

    if (processedData.type === "plotly" || activeType === "plotly") {
      const plotlyData = processedData.type === "plotly" 
        ? processedData 
        : processedData.plotlyData;

      return (
        <Plot
          data={plotlyData.data}
          layout={plotlyData.layout}
          config={plotlyData.config}
          style={{ width: "100%", height: "100%" }}
          useResizeHandler={true}
        />
      );
    }

    if (activeType === "apex" && processedData.apexData) {
      return (
        <ReactApexChart
          options={processedData.apexData.options}
          series={processedData.apexData.series}
          type={processedData.apexData.options.chart.type}
          height={520}
        />
      );
    }

    return <div className="chart-error"><p>Unsupported chart type</p></div>;
  };

  if (isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">
          <div className="spinner"></div>
          <p>Loading chart...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="chart-container enhanced"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="chart-header">
        <h3>{title}</h3>
        {processedData?.type === "dual" && (
          <ChartTypeSelector
            currentType={chartType}
            onTypeChange={setChartType}
            availableTypes={availableChartTypes}
          />
        )}
      </div>
      
      {showMetrics && processedData?.values && (
        <ChartMetrics data={processedData.values} title={title} />
      )}
      
      <div className="plot-holder">
        {renderChart()}
      </div>
    </motion.div>
  );
}

export default EnhancedChart;
